/**
 * search-field-scope.js
 *
 * The browser-side glue for storefront search. Two jobs:
 *
 *   1. **Scope the query.** Shopify's default search treats every word in a
 *      product description as a search target. Suppliers routinely write
 *      "Compatible with the OEM saddlebags" on exhausts, brake pads and seats,
 *      so a search for "saddlebag" surfaced dozens of unrelated products.
 *      Restricting matches to title / tag / product_type / vendor is the only
 *      way to silence description noise without replacing storefront search
 *      with a third-party app.
 *
 *   2. **Normalise the spelling.** Scoping alone left Shopify's literal token
 *      matching exposed: "camshaft", "cam shaft" and "cam-shaft" returned 234,
 *      45 and 45 products. search-normalize.js expands each word into the
 *      spellings the catalog actually uses, so all three now return 310.
 *
 * Coverage:
 *   - Form-submit interception (capture phase) — every in-site search where
 *     the customer types into the box and submits.
 *   - /search page-load rewrite — external links, pasted URLs, the predictive
 *     "View all results" link, bookmarks. Costs one replace() redirect when
 *     triggered, then the URL stays canonical.
 *   - Zero-result relaxation — one retry with a broader query rather than
 *     showing an empty page.
 *
 * Things we deliberately do NOT touch:
 *   - Predictive search suggestions (the typeahead). Those are short-lived and
 *     customers expect them to be liberal. Normalisation kicks in once they
 *     commit by submitting or clicking through.
 *   - Already-scoped queries, detected via the `title:` / `tag:` prefix, so
 *     running this twice over a URL is a no-op.
 *
 * The human query travels alongside the machine one as `sq`, because a scoped
 * query with spelling variants can no longer be reversed into what the
 * customer typed. `sq` is what gets shown in the search box and the results
 * heading; `q` is what Shopify searches.
 *
 * Trade-off, unchanged: customers can't find a product by a word that appears
 * only in its description body. For a catalog shopped by part name, brand or
 * category, that's the right call.
 */

(function () {
  'use strict';

  var Normalize = window.SearchNormalize;
  if (!Normalize) return; /* asset failed to load — leave native search alone */

  /** Human-readable query, carried beside the machine-generated `q`. */
  var DISPLAY_PARAM = 'sq';
  /** Marks a page we already relaxed, so a broad retry can't loop. */
  var RELAXED_PARAM = 'srx';
  var CACHE_KEY = 'forty5:search-lexicon:v1';
  /** Maps a scoped query back to what the customer typed. See displayTerms. */
  var TERMS_CACHE_PREFIX = 'forty5:search-terms:';
  /* If the lexicon is still in flight this long after submit, go without it.
     Degraded normalisation beats making someone wait on a search box. */
  var LEXICON_TIMEOUT_MS = 600;

  var lexiconUrl = (document.currentScript && document.currentScript.dataset.lexiconUrl) || null;
  var lexicon = null;
  var lexiconPromise = null;

  /* ------------------------------------------------------------------ */
  /* Lexicon loading                                                     */
  /* ------------------------------------------------------------------ */

  function loadLexicon() {
    if (lexiconPromise) return lexiconPromise;
    if (!lexiconUrl) {
      lexiconPromise = Promise.resolve(null);
      return lexiconPromise;
    }

    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        lexicon = new Normalize.Lexicon(JSON.parse(cached));
        lexiconPromise = Promise.resolve(lexicon);
        return lexiconPromise;
      }
    } catch (_e) {
      /* unavailable or corrupt — fall through to the network */
    }

    lexiconPromise = fetch(lexiconUrl, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('search-lexicon fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch (_e) {
          /* over quota — just lose the cache */
        }
        lexicon = new Normalize.Lexicon(data);
        return lexicon;
      })
      .catch(function () {
        /* Normalisation degrades gracefully without it; never block search. */
        return null;
      });

    return lexiconPromise;
  }

  /** Resolves with the lexicon, or null if it is taking too long. */
  function lexiconWithin(ms) {
    return Promise.race([
      loadLexicon(),
      new Promise(function (resolve) {
        setTimeout(function () {
          resolve(lexicon);
        }, ms);
      }),
    ]);
  }

  /* ------------------------------------------------------------------ */
  /* URL helpers                                                         */
  /* ------------------------------------------------------------------ */

  function isSearchPath(pathname) {
    return /(^|\/)search\/?$/.test(pathname || '');
  }

  function isSearchAction(action) {
    if (!action) return false;
    try {
      return isSearchPath(new URL(action, window.location.origin).pathname);
    } catch (_e) {
      return /\/search\/?$/.test(action);
    }
  }

  function go(url) {
    window.location.assign(url.toString());
  }

  /* ------------------------------------------------------------------ */
  /* Submit interception                                                 */
  /* ------------------------------------------------------------------ */

  /*
   * Capture phase, so we run before the form's own submit handlers.
   *
   * We must NOT write the scoped string into the visible input: if the native
   * submit is later prevented (predictive search cancels it when a suggestion
   * is arrow-selected — see predictive-search.js onFormSubmit), the raw
   * `title:x OR …` text would be left sitting in the focused box with nothing
   * to clean it up. Instead we scope the query and drive the navigation
   * ourselves, leaving the displayed value untouched.
   */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (!isSearchAction(form.getAttribute('action'))) return;

      var qInput = form.querySelector('input[name="q"]');
      if (!qInput) return;
      /* Customer picked a predictive suggestion — let the theme navigate to
         that product instead of hijacking into a scoped search. */
      if (form.querySelector('[aria-selected="true"] a')) return;

      var raw = Normalize.decodeEntities(qInput.value || '').trim();
      if (!raw || Normalize.isScoped(raw)) return;

      /* Collect the other form fields now: the form may be re-rendered before
         the lexicon promise settles. */
      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        params.append(key, value);
      });
      var action = form.getAttribute('action') || '/search';

      e.preventDefault();
      e.stopImmediatePropagation();

      lexiconWithin(LEXICON_TIMEOUT_MS).then(function (lex) {
        var scoped = Normalize.buildQuery(raw, lex);
        var url = new URL(action, window.location.origin);
        params.set('q', scoped || raw);
        params.set(DISPLAY_PARAM, raw);
        params.delete(RELAXED_PARAM);
        url.search = params.toString();
        go(url);
      });
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Displayed terms                                                     */
  /* ------------------------------------------------------------------ */

  /*
   * The human terms travel in `sq`, but not every URL on the results page
   * carries it: facet links, sort changes and pagination are all built from
   * `q` alone by the theme's own code. So the first time we see a pairing we
   * remember it, and recover the terms from the scoped query after that.
   */
  function rememberTerms(scoped, terms) {
    if (!scoped || !terms) return;
    try {
      sessionStorage.setItem(TERMS_CACHE_PREFIX + scoped, terms);
    } catch (_e) {
      /* unavailable or over quota — the term just won't survive filtering */
    }
  }

  function recallTerms(scoped) {
    if (!scoped) return null;
    try {
      return sessionStorage.getItem(TERMS_CACHE_PREFIX + scoped);
    } catch (_e) {
      return null;
    }
  }

  function displayTerms() {
    var params;
    try {
      params = new URL(window.location.href).searchParams;
    } catch (_e) {
      return null;
    }
    var scoped = params.get('q');
    var sq = params.get(DISPLAY_PARAM);
    if (sq) {
      var terms = Normalize.decodeEntities(sq);
      rememberTerms(scoped, terms);
      return terms;
    }
    return recallTerms(scoped);
  }

  /**
   * Recover something readable from a scoped query that predates the `sq`
   * param (old bookmarks, shared links). Those queries carry exactly one
   * `title:` per word, so the first term of each is the word that was typed.
   * Newer queries always arrive with `sq`, so this stays a fallback.
   */
  function legacyUnscope(scoped) {
    var re = /\btitle:(\([^)]*\)|[^\s)]+)/gi;
    var seen = Object.create(null);
    var out = [];
    var m;
    while ((m = re.exec(scoped)) !== null) {
      var value = m[1];
      if (value.charAt(0) === '(' && value.charAt(value.length - 1) === ')') {
        value = value.slice(1, -1);
      }
      if (value && !(value in seen)) {
        seen[value] = true;
        out.push(value);
      }
    }
    return out.join(' ');
  }

  function restoreInput(input, terms) {
    if (!input) return;
    /* Rewrite both the live property and the `value` *attribute*: the search
       modal re-seeds its input from the attribute when the <details> opens
       (and on form reset), so cleaning only the property lets the raw scoped
       string reappear the moment the modal is opened. */
    if (Normalize.isScoped(input.value)) input.value = terms || legacyUnscope(input.value);
    else if (terms && !input.value) input.value = terms;

    var attr = input.getAttribute('value');
    if (attr && Normalize.isScoped(attr)) input.setAttribute('value', terms || legacyUnscope(attr));
    else if (terms && !attr) input.setAttribute('value', terms);
  }

  function restoreDisplay() {
    var terms = displayTerms();
    var inputs = document.querySelectorAll('input[name="q"]');
    for (var i = 0; i < inputs.length; i++) restoreInput(inputs[i], terms);

    if (!terms) return;
    /* main-search.liquid renders the searched-for term into these slots and
       leaves them empty for machine-generated queries, because Liquid has no
       access to the `sq` param. */
    var slots = document.querySelectorAll('[data-search-term]');
    for (var s = 0; s < slots.length; s++) slots[s].textContent = terms;
  }

  /* ------------------------------------------------------------------ */
  /* Landing on /search                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Rewrite a raw `q` that arrived from outside this code (external link,
   * pasted URL, the predictive "View all results" link). One redirect on
   * landing; navigation from then on is already scoped.
   */
  function rewriteRawQuery(url) {
    var raw = url.searchParams.get('q');
    if (!raw || Normalize.isScoped(raw)) return false;

    loadLexicon().then(function (lex) {
      url.searchParams.set('q', Normalize.buildQuery(raw, lex) || raw);
      url.searchParams.set(DISPLAY_PARAM, raw);
      window.location.replace(url.toString());
    });
    return true;
  }

  /**
   * A scoped search that found nothing gets one broader retry rather than a
   * dead end: drop the rarest word, or for a single word hand it to Shopify's
   * native unscoped search. `srx` marks the retry so it can't loop.
   */
  function relaxIfEmpty(url) {
    if (url.searchParams.get(RELAXED_PARAM)) return;

    var results = document.querySelector('[data-search-results-count]');
    if (!results || results.getAttribute('data-search-results-count') !== '0') return;

    var terms = displayTerms();
    if (!terms) return;

    loadLexicon().then(function (lex) {
      var relaxed = Normalize.relaxQuery(terms, lex);
      if (!relaxed || !relaxed.query) return;
      url.searchParams.set('q', relaxed.query);
      url.searchParams.set(DISPLAY_PARAM, terms);
      url.searchParams.set(RELAXED_PARAM, '1');
      window.location.replace(url.toString());
    });
  }

  /**
   * Tell the customer their exact words found nothing, before they wonder why
   * the results look loose. Suppressed when the retry came up empty too —
   * "showing the closest results" above an empty page reads as a bug.
   */
  function showRelaxedNotice() {
    var results = document.querySelector('[data-search-results-count]');
    if (results && results.getAttribute('data-search-results-count') === '0') return;
    var notice = document.querySelector('[data-search-relaxed-notice]');
    if (notice) notice.removeAttribute('hidden');
  }

  function onReady() {
    restoreDisplay();

    if (!isSearchPath(window.location.pathname)) return;
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_e) {
      return; /* URL parsing unavailable — accept whatever rendered */
    }
    if (rewriteRawQuery(url)) return;
    if (url.searchParams.get(RELAXED_PARAM)) showRelaxedNotice();
    else relaxIfEmpty(url);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();

  /* The header search lives inside a <details> modal that is closed on load,
     and some theme code re-seeds its input from the `value` attribute when the
     modal opens. Re-clean on toggle, and on focus as a backstop. Capture phase
     so we run before predictive search reads the value. Focus is also the cue
     to start fetching the lexicon: by the time anyone finishes typing a part
     name it is long since cached. */
  document.addEventListener(
    'toggle',
    function () {
      loadLexicon();
      restoreDisplay();
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input[name="q"]')) {
        loadLexicon();
        restoreInput(t, displayTerms());
      }
    },
    true
  );
})();
